<app-auth-shell
  panelClass="max-w-[980px]"
  mainClass="items-start lg:items-center"
>
  <div class="border-b border-textbox-border px-6 py-6 sm:px-8 lg:px-10">
    <div
      class="mb-3 inline-flex rounded-full border border-companion-teal-border bg-companion-teal-soft px-3 py-1 inter-500 text-[12px] text-companion-teal"
    >
      New workspace
    </div>
    <h1
      class="font-spaceGrotesk text-[30px] font-semibold leading-tight text-inverted-neutral sm:text-[38px]"
    >
      Create your Kekeno Surveys account
    </h1>
    <p class="mt-3 max-w-2xl inter-300 text-[15px] leading-6 text-support-text">
      Choose the account type, add your details, and set up your workspace.
    </p>
  </div>

  <!-- Scrollable content area -->
  <div class="flex-1 overflow-y-auto">
    <div class="flex flex-col bg-surface-background text-support-text">

      <!-- Account Type Section -->
      @if (currentStep === 0) {
        <div class="grid grid-cols-1 lg:grid-cols-[1fr_min-content_1fr] w-full">
          <!-- Personal Account -->
          <label class="card-body p-4 lg:p-8 flex items-center justify-center lg:justify-between flex-col w-full gap-4 lg:gap-6 cursor-pointer" matRipple>
            <h2 class="font-montserrat font-semibold pt-[10px] lg:pt-[30px] text-[20px] lg:text-[32px]">Personal</h2>
            <div class="w-full lg:w-auto flex justify-center overflow-hidden">
              <span class="material-symbols-outlined text-6xl lg:text-8xl leading-none text-inverted-neutral" aria-hidden="true">person</span>
            </div>
            <p class="font-montserrat text-center text-[14px] lg:text-[16px]">Create an account for one person. Use this for work, school, or personal projects.</p>
            <span class="flex flex-row justify-end w-full">
              <input
                [checked]="registerForm.controls.accountType.value === AccountType.Personal"
                (change)="updateAccountType($event)"
                [value]="AccountType.Personal"
                type="radio"
                name="account-type"
                class="radio"
                />
              </span>
            </label>
            <!-- Vertical Divider -->
            <div class="hidden lg:block h-[90%] w-[1px] bg-inverted-neutral m-auto rounded-full"></div>
            <!-- Horizontal Divider (For Small Screen Size) -->
            <div class="lg:hidden w-[90%] h-[1px] bg-inverted-neutral m-auto rounded-full"></div>
            <!-- Organisation Account -->
            <label class="card-body p-4 lg:p-8 flex items-center justify-center lg:justify-between flex-col h-full w-full gap-4 lg:gap-6 cursor-pointer" matRipple>
              <h2 class="font-montserrat font-semibold pt-[10px] lg:pt-[30px] text-[20px] lg:text-[32px]">Organisation</h2>
              <div class="w-full lg:w-auto flex justify-center overflow-hidden">
                <span class="material-symbols-outlined text-6xl lg:text-8xl leading-none text-inverted-neutral" aria-hidden="true">groups</span>
              </div>
              <p class="font-montserrat text-center text-[14px] lg:text-[16px]">Create an account for an organisation. Create a super admin account to manage your team. Add as many users as you need.</p>
              <span class="flex flex-row justify-end w-full">
                <input
                  [checked]="registerForm.controls.accountType.value === AccountType.Organisation"
                  (change)="updateAccountType($event)"
                  [value]="AccountType.Organisation"
                  type="radio"
                  name="account-type"
                  class="radio"
                  />
                </span>
              </label>
            </div>
          }

          <!-- Personal Details Section -->
          @if (currentStep === 1) {
            <div class="card-body p-6 lg:p-8 flex flex-col items-center justify-center w-full">
              <h2 class="font-montserrat font-semibold text-[20px] lg:text-[32px] mt-[10px] mb-[30px]">Let's get started</h2>
              <div class="grid grid-cols-1 lg:grid-cols-2 w-full lg:w-[80%] gap-4 lg:gap-6">
                <input [formControl]="registerForm.controls.firstName" type="text" placeholder="First Name*"
                  class="form-control" />
                <input [formControl]="registerForm.controls.lastName" type="text" placeholder="Last Name*"
                  class="form-control" />
                <input [formControl]="registerForm.controls.email" type="text" placeholder="Email*"
                  class="form-control" />
                <input [formControl]="registerForm.controls.mobile" type="text" placeholder="Mobile"
                  class="form-control" />
              </div>
              <label class="flex gap-4 items-center text-[14px] lg:text-[16px] mt-[25px] lg:mt-[40px] mb-[10px] cursor-pointer">
                <input [formControl]="registerForm.controls.agreesToTermsAndConditions" type="checkbox" class="checkbox" />
                <span class="font-montserrat">
                  I agree to the
                  <a [routerLink]="['/terms-and-conditions']" class="underline hover:no-underline">Terms of Service</a>
                  and
                  <a [routerLink]="['/privacy-policy']" class="underline hover:no-underline">Privacy Policy</a>
                  *
                </span>
              </label>
              <label class="flex gap-4 items-center text-[14px] mb-[15px] lg:mb-0 lg:text-[16px] cursor-pointer">
                <input [formControl]="registerForm.controls.subscribeToEmailNotifications" type="checkbox" class="checkbox" />
                <span class="font-montserrat">I wish to receive product news and special promotions via email.</span>
              </label>
            </div>
          }

          <!-- Password Section -->
          @if (currentStep === 2) {
            <div class="card-body p-6 lg:p-8 flex flex-col items-center justify-center w-full">
              <h2 class="font-montserrat font-semibold text-[20px] lg:text-[32px] mt-[10px] mb-[30px]">Create a password</h2>
              <div class="grid grid-cols-1 w-[90%] lg:w-[60%] gap-6 lg:gap-8">
                <div class="flex flex-col">
                  <input (ngModelChange)="updatePasswordScore()" [formControl]="registerForm.controls.password" type="password" placeholder="Enter password*"
                    class="form-control" />
                  <!-- Password Strength Meter -->
                  @if (registerForm.controls.password.value) {
                    <div class="flex gap-4 mt-4">
                      <div
                        [ngClass]="passwordStrengthScore === 0 ? 'w-[40px] bg-red-500' : passwordStrengthScore === 1 ? 'w-[80px] bg-orange-500' : 'w-[120px] bg-green-500'"
                        class="rounded-[5px]"
                      ></div>
                      <span
                        [ngClass]="passwordStrengthScore === 0 ? 'text-red-500' : passwordStrengthScore === 1 ? 'text-orange-500' : 'text-green-500'"
                        class="font-montserrat"
                      >{{ passwordStrengthScore === 0 ? 'Weak' : passwordStrengthScore === 1 ? 'Average' : 'Strong' }}</span>
                    </div>
                  }
                </div>
                <div class="flex flex-col gap-4">
                  <input [formControl]="registerForm.controls.confirmPassword" type="password" placeholder="Confirm password*"
                    class="form-control" />
                  @if (registerForm.controls.password.value && registerForm.controls.confirmPassword.value && !passwordsMatch()) {
                    <div class="font-montserrat text-red-500">Passwords do not match</div>
                  }
                </div>
              </div>
            </div>
          }

          <!-- Organisation Section -->
          @if (currentStep === 3 && registerForm.controls.accountType.value === AccountType.Organisation) {
            <div class="card-body p-6 lg:p-8 flex flex-col items-center justify-center w-full">
              <h2 class="font-montserrat font-semibold text-[20px] lg:text-[32px] mt-[10px] mb-[30px]">Create an organisation</h2>
              <div class="grid grid-cols-1 lg:grid-cols-2 w-[90%] lg:w-[80%] gap-4 lg:gap-6">
                <input [formControl]="registerForm.controls.organisationDetails.controls.organisationName" type="text" placeholder="Organisation Name*"
                  class="form-control lg:col-span-2" />
                <input [formControl]="registerForm.controls.organisationDetails.controls.contactEmailAddress" type="text" placeholder="Org Email Address*"
                  class="form-control" />
                <input [formControl]="registerForm.controls.organisationDetails.controls.contactMobileNumber" type="text" placeholder="Phone Number"
                  class="form-control" />
              </div>
            </div>
          }

          <!-- Personal Account Section (Only for Creating Personal Account for Existing User) -->
          @if (currentStep === 3 && registerForm.controls.accountType.value === AccountType.Personal) {
            <div class="card-body p-6 lg:p-8 flex flex-col items-center justify-center w-full">
              <h2 class="font-montserrat text-center font-semibold text-[20px] lg:text-[32px] mt-[10px] mb-[30px]">Create a personal workspace</h2>
              <div class="grid grid-cols-2 w-[90%] lg:w-[80%] gap-8">
                <input [formControl]="registerForm.controls.personalAccountName" type="text" placeholder="Workspace Name*"
                  class="form-control col-span-2" />
              </div>
            </div>
          }
        </div>
  </div>

  <!-- Footer area (buttons + progress) — placed after the scrollable content so it sits at the bottom)
       The footer is not fixed, so when content overflows the content area will scroll and the footer remains below it. -->
  <div class="w-full flex flex-col items-center border-t border-textbox-border bg-surface-muted p-4">
    <div class="flex gap-6">
      <button matRipple (click)="previousStep()"
        class="bg-transparent border border-nav-btn-border text-nav-btn-text hover:bg-nav-btn-hover-bg hover:text-nav-btn-hover-text px-6 py-2 rounded-full inter-500 text-[1.125rem] transform transition duration-150 ease-out active:scale-[0.98] focus:outline-none">
        Back
      </button>
      <button matRipple (click)="nextStep()" [disabled]="!canMoveToNextStep() || accountCreating"
        class="bg-transparent border border-nav-btn-border text-nav-btn-text hover:bg-nav-btn-hover-bg hover:text-nav-btn-hover-text px-6 py-2 rounded-full disabled:opacity-50 disabled:cursor-not-allowed inter-500 text-[1.125rem] transform transition duration-150 ease-out active:scale-[0.98] focus:outline-none">
        @if (accountCreating) {
          <svg class="animate-spin" width="20" height="20" viewBox="0 0 25 25" fill="none" xmlns="http://www.w3.org/2000/svg">
            <g id="COCO/Line/Rotate - Right"><path id="Vector" d="M21.5059 12.7058C21.5059 17.5921 17.4764 21.5532 12.5059 21.5532C7.5353 21.5532 3.50586 17.5921 3.50586 12.7058C3.50586 7.81945 7.50586 3.8583 12.5059 3.8583C18.5059 3.8583 21.5059 8.77356 21.5059 8.77356M21.5059 8.77356L21.5059 3.55322M21.5059 8.77356H16.8508" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></g>
          </svg>
        } @else {
          Next
        }
      </button>
    </div>

    <div class="w-full max-w-2xl mt-4 px-6 pb-4">
      <div class="w-full rounded-full h-3 relative overflow-hidden bg-textbox-fill">
        <div
          class="absolute left-0 top-0 h-full rounded-full bg-kek-purple transition-all duration-500 ease-in-out"
          [style.width]="getProgressBarWidth()"
        ></div>
      </div>
    </div>
  </div>

</app-auth-shell>
